import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

export type NavIcon = React.ComponentType<React.SVGProps<SVGSVGElement> & { size?: number }>;
export type NavItem = { to: string; label: string; divider?: boolean; icon?: NavIcon };

interface NavDropdownProps {
  label: string;
  items: NavItem[];
  isActive: boolean;
}

const NavDropdown: React.FC<NavDropdownProps> = ({ label, items, isActive }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuId = useId();
  const dropdownRef = useRef<HTMLLIElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const menuItems = menuRef.current?.querySelectorAll('a');
    if (!menuItems) return;

    const currentIndex = Array.from(menuItems).findIndex(
      (item) => item === document.activeElement,
    );

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          const nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
          menuItems[nextIndex]?.focus();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
          menuItems[prevIndex]?.focus();
        }
        break;
      case 'Enter':
      case ' ': {
        if (document.activeElement === buttonRef.current) {
          e.preventDefault();
          setIsOpen(!isOpen);
        }
        break;
      }
      case 'Tab':
        if (isOpen && e.shiftKey && document.activeElement === menuItems[0]) {
          setIsOpen(false);
        } else if (isOpen && !e.shiftKey && document.activeElement === menuItems[menuItems.length - 1]) {
          setIsOpen(false);
        }
        break;
      default:
        break;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const firstItem = menuRef.current?.querySelector('a');
      firstItem?.focus();
    }
  }, [isOpen]);

  return (
    <li
      ref={dropdownRef}
      className={`nav-dropdown ${isOpen ? 'nav-dropdown-open' : ''}`}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={buttonRef}
        type="button"
        className={`nav-link nav-dropdown-trigger ${isActive ? 'nav-link-active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={menuId}
      >
        {label}
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      <ul
        ref={menuRef}
        id={menuId}
        className="nav-dropdown-menu"
        role="menu"
        aria-label={`${label} submenu`}
      >
        {items.map((item) => (
          <li key={item.to} role="none" className={item.divider ? 'dropdown-divider' : ''}>
            <Link
              to={item.to}
              role="menuitem"
              className="nav-dropdown-item"
              onClick={() => setIsOpen(false)}
              tabIndex={isOpen ? 0 : -1}
            >
              {item.icon && <item.icon size={16} aria-hidden="true" />}
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
};

export default NavDropdown;
